// Worker Manager for SQLite OPFS operations
import { SceneObject } from './service'

class SQLiteWorkerManager {
  private worker: Worker | null = null
  private messageId = 0
  private pendingMessages = new Map<number, { resolve: Function, reject: Function }>()
  private initialized = false
  private sessionId: string

  constructor() {
    this.sessionId = this.getSessionId()
  }

  private getSessionId(): string {
    let sessionId = localStorage.getItem('geometric-model-session-id')
    if (!sessionId) {
      sessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      localStorage.setItem('geometric-model-session-id', sessionId)
    }
    return sessionId
  }

  async init(): Promise<{ success: boolean, persistent?: boolean, error?: string }> {
    if (this.initialized && this.worker) {
      return { success: true }
    }

    console.log('[WorkerManager] Attempting to initialize worker...')

    try {
      // Create the worker using Vite's worker import syntax
      this.worker = new Worker(
        new URL('../workers/sqlite-worker.ts', import.meta.url),
        { type: 'module' }
      )
      console.log('[WorkerManager] Worker created successfully')
      
      // Set up message handling
      this.worker.addEventListener('message', (event) => {
        const { id, success, data, error } = event.data
        const pending = this.pendingMessages.get(id)
        
        if (pending) {
          this.pendingMessages.delete(id)
          if (success) {
            pending.resolve(data)
          } else {
            pending.reject(new Error(error))
          }
        }
      })

      this.worker.addEventListener('error', (error) => {
        console.error('[WorkerManager] Worker error:', error)
        this.initialized = false
      })

      this.worker.addEventListener('messageerror', (error) => {
        console.error('[WorkerManager] Worker message error:', error)
      })

      // Initialize the worker
      const result = await this.sendMessage('INIT', { sessionId: this.sessionId })
      this.initialized = result.success
      
      console.log('[WorkerManager] Worker initialized:', result)
      return result
    } catch (error) {
      console.error('[WorkerManager] Failed to initialize worker:', error)
      return { success: false, error: error instanceof Error ? error.message : String(error) }
    }
  }

  private sendMessage(type: string, data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        reject(new Error('Worker not initialized'))
        return
      }

      const id = ++this.messageId
      this.pendingMessages.set(id, { resolve, reject })

      this.worker.postMessage({ type, data, id })

      // Timeout after 30 seconds
      setTimeout(() => {
        if (this.pendingMessages.has(id)) {
          this.pendingMessages.delete(id)
          reject(new Error('Worker message timeout'))
        }
      }, 30000)
    })
  }

  async saveSceneState(objects: SceneObject[], action: string, sceneId = 'main-scene'): Promise<void> {
    if (!this.initialized) {
      await this.init()
    }

    const result = await this.sendMessage('SAVE_SCENE', { objects, action, sceneId })
    
    if (!result.success) {
      throw new Error(result.error)
    }

    console.log(`[WorkerManager] Saved scene state: ${action} (${result.count} objects)`)
  }

  async loadSceneState(sceneId = 'main-scene'): Promise<SceneObject[]> {
    if (!this.initialized) {
      await this.init()
    }

    const result = await this.sendMessage('LOAD_SCENE', { sceneId })
    
    if (!result.success) {
      throw new Error(result.error)
    }

    if (!result.found) {
      console.log('[WorkerManager] No saved scene found in database')
      return []
    }

    console.log(`[WorkerManager] Loaded scene state (${result.objects.length} objects)`)
    return result.objects
  }

  isAvailable(): boolean {
    return this.initialized && this.worker !== null
  }

  getSessionInfo(): { sessionId: string, isAvailable: boolean, isPersistent: boolean } {
    return {
      sessionId: this.sessionId,
      isAvailable: this.isAvailable(),
      isPersistent: true // Worker-based OPFS should support persistence
    }
  }

  destroy(): void {
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
    this.initialized = false
    this.pendingMessages.clear()
  }
}

// Create singleton instance
export const sqliteWorkerManager = new SQLiteWorkerManager()

// Fallback localStorage-based persistence for when worker fails
class LocalStoragePersistence {
  private storageKey = 'geometric-models-scene'

  async saveSceneState(objects: SceneObject[], action: string): Promise<void> {
    try {
      const sceneData = {
        objects,
        action,
        timestamp: new Date().toISOString(),
        sessionId: this.getSessionId()
      }
      
      localStorage.setItem(this.storageKey, JSON.stringify(sceneData))
      console.log(`[LocalStorage] Saved scene state: ${action} (${objects.length} objects)`)
    } catch (error) {
      console.error('[LocalStorage] Failed to save scene state:', error)
      throw error
    }
  }

  async loadSceneState(): Promise<SceneObject[]> {
    try {
      const stored = localStorage.getItem(this.storageKey)
      if (!stored) {
        console.log('[LocalStorage] No saved scene found')
        return []
      }

      const sceneData = JSON.parse(stored)
      console.log(`[LocalStorage] Loaded scene state (${sceneData.objects.length} objects)`)
      return sceneData.objects || []
    } catch (error) {
      console.error('[LocalStorage] Failed to load scene state:', error)
      return []
    }
  }

  private getSessionId(): string {
    return localStorage.getItem('geometric-model-session-id') || 'default-session'
  }

  isAvailable(): boolean {
    return typeof localStorage !== 'undefined'
  }

  getSessionInfo(): { sessionId: string, isAvailable: boolean, isPersistent: boolean } {
    return {
      sessionId: this.getSessionId(),
      isAvailable: this.isAvailable(),
      isPersistent: false // localStorage clears on browser data clear
    }
  }
}

export const localStoragePersistence = new LocalStoragePersistence() 